import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, Platform, ModalController } from '@ionic/angular';
import { BehaviorSubject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { CategoryInterface, SubCategoryInterface } from 'src/app/interfaces/lobby.interface';
import { CdnIconComponent } from '../cdn-icon/cdn-icon.component';
import { ControlHelperService } from 'src/app/services/control/control-helper.service';
import { ControlService } from 'src/app/services/control/control.service';
import { DragScrollDirective } from 'src/app/directives/drag-scroll.directive';
import { EventsService } from 'src/app/services/event/event.service';
import { SearchOverlayComponent } from '../search/search.component';
import { StructureHelperService } from 'src/app/services/structure/structure-helper.service';
import { StructureService } from 'src/app/services/structure/structure.service';
import { ZoomHelperService } from 'src/app/services/zoom-helper/zoom-helper.service';
import { ConfigService } from 'src/app/services/config/config.service';

@UntilDestroy()
@Component({
  selector: 'app-catbar',
  templateUrl: './catbar.component.html',
  styleUrls: ['./catbar.component.scss'],
  standalone: true,
  imports: [
    CdnIconComponent,
    CommonModule,
    DragScrollDirective,
    IonicModule,
  ]
})
export class CatbarComponent implements OnInit {

  public activeCategory: CategoryInterface | null;
  public activeLobbyCategory: CategoryInterface | null;
  public activeSubCategory: SubCategoryInterface | null;
  public androidBackButtonEvent: Subscription;
  public categories: CategoryInterface[] = []
  public defaultCategory: CategoryInterface;
  public disableScroll = false;
  public gameMode: boolean;
  public isIosApp: boolean = false
  public isMobile: boolean;
  public isMobileWeb: boolean;
  public isZoomButtonsEnabled: boolean = false;
  public scrollElementLeft = new BehaviorSubject<any>(null)
  public scrollElementRight = new BehaviorSubject<any>(null)
  public showLeftButton = false;
  public showRightButton = false;
  public showScrollButtons: boolean;
  public showSearchInCategory: boolean;
  public showTestingData: boolean = false;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private config: ConfigService,
    private control: ControlService,
    private controlHelper: ControlHelperService,
    private eventsService: EventsService,
    private modalController: ModalController,
    private platform: Platform,
    private structure: StructureService,
    private structureHelper: StructureHelperService,
    private zoomHelperService: ZoomHelperService,
  ) {
    this.showSearchInCategory = this.config.isEnabled('FEATURE_LOBBY_SEARCH_ONACTIVECATEGORY');
    this.showScrollButtons = this.config.isEnabled('FEATURE_LOBBY_CATBAR_SHOWSCROLLBTNS', true);
    this.showTestingData = this.config.isEnabled('TESTING_LOBBY_SELECTED_ID', false);
  }

  ngOnInit(): void {
    this.isMobile = this.platform.is("capacitor") || this.platform.is('mobileweb');
    this.isMobileWeb = this.platform.is('mobileweb');
    this.isIosApp = this.platform.is('ios') && this.platform.is('capacitor');
    this.getDefaultCategory();
    this.structureCatbar();
    this.getActiveCategory();
    this.getActiveLobbyCategory();
    this.getActiveSubCategory();
    this.subscribeToGameGridComponent();
    if (this.platform.is("android")) this.checkAndroidBackButtons();

    this.eventsService.subscribe("event:balanceState", (data) => this.gameMode = !data?.show);
  }

  private checkAndroidBackButtons() {
    if (this.androidBackButtonEvent) {
      this.androidBackButtonEvent.unsubscribe();
    }
    this.androidBackButtonEvent = this.eventsService.subscribe("event:android:backbutton", (event) => {
      if (this.activeCategory?.id !== this.defaultCategory?.id) {
        this.dismissSelectedCategory();
        return;
      }
    });
  }

  private structureCatbar() {
    this.structure.getCategoriesByPlacement('MAIN')
      .pipe(
        map(categories => categories?.filter(item => !item.hide_category)),
        untilDestroyed(this),
      )
      .subscribe((categories: CategoryInterface[]) => {
        this.categories = categories;
        if (this.control?.depositFlow) {
          this.control.depositFlow = false;
          if (this.activeCategory?.id == this.defaultCategory?.id && !this.gameMode && !this.activeSubCategory) {
            this.control.goToDefaultCategory();
          }
        } else {
          this.control.goToDefaultCategory();
        }
      });
  }

  private getDefaultCategory(): void {
    this.structureHelper.getDefaultCategory()
      .pipe(
        untilDestroyed(this),
        take(1)
      )
      .subscribe((category) => {
        this.defaultCategory = category;
      });
  }

  private getActiveCategory(): void {
    this.control.selectedCategory$
      .pipe(untilDestroyed(this))
      .subscribe((cat: CategoryInterface) => {
        this.activeCategory = cat;
      });
  }

  private getActiveLobbyCategory(): void {
    this.controlHelper.activeLobbyCategory$
      .pipe(untilDestroyed(this))
      .subscribe((cat: SubCategoryInterface) => {
        this.activeLobbyCategory = cat;
      });
  }

  private getActiveSubCategory(): void {
    this.control.selectedSubCategory$
      .pipe(untilDestroyed(this))
      .subscribe((cat: SubCategoryInterface) => {
        this.activeSubCategory = cat;
      });
  }

  private getLastActiveCategory(): void {
    this.controlHelper.lastActiveLobbyCategory$
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        untilDestroyed(this)
      )
      .subscribe((cat: CategoryInterface) => {
        if (cat) {
          this.control.selectCategory(cat);
          this.controlHelper.setActiveLobbyCategory(cat);
        } else {
          this.dismissSelectedCategory();
        }
      });
  }

  public selectCategory(cat: any): void {
    if (cat.id === this.activeCategory?.id || cat.id === this.activeSubCategory?.id) {
      return console.warn('[ControlService: SelectCategory]: Same category received.');
    }

    this.control.selectCategory(cat);
    this.controlHelper.setActiveLobbyCategory(cat);
  }

  public dismissSelectedCategory(): void {
    this.control.goToDefaultCategory();
  }

  public dismissNonActiveCategory(): void {
    this.getLastActiveCategory();
  }

  public dismissSelectedSubCategory(): void {
    this.getLastActiveCategory();
  }

  public openSearch() {
    this.structureHelper.getAllGames().pipe(take(1)).subscribe(async (games) => {
      const modal = await this.modalController.create({
        component: SearchOverlayComponent,
        cssClass: 'search-overlay-desktop',
        componentProps: {
          allGamesCategoryData: games
        }
      });

      await modal.present();

      const { data } = await modal.onDidDismiss();
      if (data && data.filteredGames) {
        console.log('Modal dismissed, filtered games received:', data.filteredGames);
      } else {
        console.log('Modal dismissed without filtered games data.');
      }
    });
  }

  //Scrolling Methods Starts

  public reachesLeft($event) {
    this.showLeftButton = !$event;
    this.changeDetectorRef.detectChanges();
  }

  public reachesRight($event) {
    this.showRightButton = !$event;
    this.changeDetectorRef.detectChanges();
  }

  public scrollCatsRight() {
    this.scrollElementRight.next(1);
    this.setDisableScroll();
  }

  public scrollCatsLeft() {
    this.scrollElementLeft.next(1);
    this.setDisableScroll();
  }

  private setDisableScroll() {
    this.disableScroll = true;

    setTimeout(() => this.disableScroll = false, 1000);
  }

  //Scrolling Methods Ends

  public zoomOut() {
    this.eventsService.publish('event:zoom:level:changed', this.zoomHelperService.zoomLevel + 1);
  }

  public zoomIn() {
    this.eventsService.publish('event:zoom:level:changed', this.zoomHelperService.zoomLevel - 1);
  }

  private zoomButtonsEnabled(): boolean {
    return this.config.isEnabled('FEATURE_ZOOM_BUTTONS_ENABLED', false) && !this.platform.is('desktop');
  }

  private subscribeToGameGridComponent(): void {
    this.eventsService.subscribeUntilDestroyed('event:game-grid-view-changed', (visible: boolean) => {
      this.isZoomButtonsEnabled = this.zoomButtonsEnabled() && visible;
    }, this);
  }
}


